import React, { useState } from "react";

import { useMutation, useQuery } from "@apollo/client";
import {
  Box,
  CircularProgress,
  FormControl,
  Grid,
  styled,
  Typography,
} from "@mui/material";
import Badge from "@mui/material/Badge";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { isEmpty } from "lodash";
import { makeStyles, withStyles } from "tss-react/mui";

import DeleteModal from "@/components/DeleteModal";
import FormPaperIcon from "@/components/Icons/FormPaperIcon";
import NutricoachIcon from "@/components/Icons/NutricoachIcon";
import { graphql } from "@/gql";
import { MyFormFragment, OrderByDirection } from "@/gql/graphql";
import useDialogState from "@/hooks/useDialogState";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { colors } from "@/theme/overrides/colors";
import { format } from "@/utils/datetime";
import { displayError } from "@/utils/errors";
import { FragmentType } from "@/utils/type";

import OptionMenu from "./components/OptionMenu";
import SentFormClientMenu from "./components/SentFormsClientMenu";
import { formsPageQuery } from "../FormsPage";

const Root = styled("div")({
  height: "100vh",
  backgroundColor: colors.white,
  padding: 20,
});

const useStyles = makeStyles()({
  mainContentWrapper: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    flexWrap: "wrap",
    margin: "auto",
  },
  emptyContentWrapper: { margin: "auto" },
  emptyLabel: {
    color: "#AAB0BB",
    fontWeight: 700,
    display: "flex",
    justifyContent: "center",
  },
  emptyIcon: {
    display: "flex",
    justifyContent: "center",
    marginTop: "20%",
  },
  formContentWrapper: {
    width: "auto",
    minHeight: 280,
    border: "1px solid #E8EEF8",
    cursor: "pointer",
    margin: "auto",
  },
  imageWrapper: {
    width: "auto",
    height: "190px",
    border: "1px solid #D4DBE5",
    marginTop: 12,
    marginLeft: "auto",
    marginRight: "auto",
    background: "#D4DBE5",
  },
  image: {
    objectFit: "contain",
    width: "100%",
    height: "100%",
  },
  labelWrapper: {
    width: "100%",
    marginLeft: "auto",
    marginRight: "auto",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 12,
  },
  formName: {
    fontSize: 12,
    fontWeight: 700,
  },
  formDate: {
    fontSize: 10,
    fontWeight: 700,
    color: colors.grey,
  },
  sort: {
    width: 182,
  },
});

interface Props {
  myForms: FragmentType<typeof myFormsFragment>[];
  loading: boolean;
  onPreviewForm: (data: MyFormFragment | null, isSending: boolean) => void;
  order: OrderByDirection;
  handleSort: (event: SelectChangeEvent) => void;
}

const StyledNutricoachLogo = withStyles(NutricoachIcon, {
  root: {
    maxHeight: 24,
    maxWidth: 24,
  },
});

const myFormsFragment = graphql(`
  fragment MyForm on Form {
    id
    title
    previewUrl
    thumbnailUrl
    pdfUrl
    createdAt
    updatedAt
    coach {
      id
      profile {
        id
        fullName
      }
    }
    sentTo {
      nodes {
        id
        email
        profile {
          id
          fullName
          avatarUrl
        }
      }
    }
  }
`);

const coachViewerIdQuery = graphql(`
  query CoachViewerId {
    coachViewer {
      id
    }
  }
`);

const deleteFormMutation = graphql(`
  mutation DeleteForm($input: DeleteFormInput!) {
    deleteForm(input: $input) {
      form {
        id
      }
    }
  }
`);

const MyForms: React.FC<Props> = ({
  myForms,
  loading,
  onPreviewForm,
  order,
  handleSort,
}) => {
  const { classes } = useStyles();
  const deleteModal = useDialogState();
  const [currentForm, setCurrentForm] = useState<MyFormFragment | null>(null);
  const [deleteForm] = useMutation(deleteFormMutation);
  const snackbar = useGlobalSnackbar();

  const { data } = useQuery(coachViewerIdQuery);
  const coachId = data?.coachViewer.id;
  const handleMenuClick = (type: string, data: MyFormFragment): void => {
    switch (type) {
      case "delete":
        setCurrentForm(data);
        return deleteModal.show();
      case "preview":
        return onPreviewForm(data, false);
      case "send":
        return onPreviewForm(data, true);
      default:
        return;
    }
  };

  const handleDeleteForm = async () => {
    if (currentForm == null) return;
    try {
      const result = await deleteForm({
        variables: { input: { id: currentForm.id } },
        refetchQueries: [
          {
            query: formsPageQuery,
            variables: { title: "", orderByTitle: order },
          },
        ],
      });
      if (result.data) snackbar.open("Form successfully deleted!");
    } catch (error) {
      snackbar.open(displayError(error, "Unable to delete form"));
    }
  };

  const _mapForm = (form: MyFormFragment) => {
    return (
      <React.Fragment key={form.id}>
        <Grid item xs={12} md={4} lg={2}>
          <Badge
            badgeContent={
              <>{coachId != form.coach.id && <StyledNutricoachLogo />}</>
            }
          >
            <Box className={classes.formContentWrapper}>
              <Box className={classes.imageWrapper}>
                <img className={classes.image} src={form.thumbnailUrl} />
              </Box>
              <Box className={classes.labelWrapper}>
                <Box>
                  <Typography className={classes.formName}>
                    {form.title}
                  </Typography>
                  <Box>
                    <Typography className={classes.formDate}>
                      {format({ datetime: form?.createdAt }, "dd MMM, yyyy")}
                    </Typography>
                  </Box>
                  <SentFormClientMenu data={form} />
                </Box>
                <Box>
                  <OptionMenu
                    coachId={coachId ?? ""}
                    ownerId={form.coach.id}
                    formId={form.id}
                    onClickMenu={(type) => handleMenuClick(type, form)}
                  />
                </Box>
              </Box>
            </Box>
          </Badge>
        </Grid>
      </React.Fragment>
    );
  };

  if (loading) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        width="100%"
        padding={14}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Root>
      <DeleteModal
        visible={deleteModal.shown}
        onClose={deleteModal.hide}
        title="Delete form"
        message="Are you sure you want to delete this form?"
        onDelete={handleDeleteForm}
      />
      <Box display="flex" flexDirection="row" justifyContent="flex-end">
        <Select className={classes.sort} value={order} onChange={handleSort}>
          <MenuItem value={OrderByDirection.Asc}>Ascending</MenuItem>
          <MenuItem value={OrderByDirection.Desc}>Descending</MenuItem>
        </Select>
      </Box>
      <Grid container className={classes.mainContentWrapper} spacing={2}>
        {myForms && myForms.map(_mapForm)}
      </Grid>
      {isEmpty(myForms) && (
        <Box className={classes.emptyContentWrapper}>
          <Box className={classes.emptyIcon}>
            <FormPaperIcon />
          </Box>
          <Typography className={classes.emptyLabel}>No Forms yet</Typography>
        </Box>
      )}
    </Root>
  );
};

export default MyForms;
